import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import { useAuth } from '../../contexts/AuthContext';
import { getTodayAbsensiForMap, getAbsensiMapByDate, getSekolahList, getInstansiList } from '../../lib/api';
import { formatTime, getStatusLabel, getStatusColor } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ChevronLeft, Loader2, RefreshCw, MapPin, School, Users, Clock, ShieldCheck, ShieldAlert, Filter, CalendarIcon, History, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../ui/sonner';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';

// Fix leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

const schoolIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background: #1e1b4b; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
      <polyline points="9 22 9 12 15 12 15 22"></polyline>
    </svg>
  </div>`,
  iconSize: [36, 36], iconAnchor: [18, 36], popupAnchor: [0, -36]
});

const instansiIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background: #1e3a5f; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
      <line x1="9" y1="2" x2="9" y2="22"></line>
      <line x1="15" y1="2" x2="15" y2="22"></line>
      <line x1="4" y1="12" x2="20" y2="12"></line>
    </svg>
  </div>`,
  iconSize: [36, 36], iconAnchor: [18, 36], popupAnchor: [0, -36]
});

const createAttendanceIcon = (status, isInRadius, faceVerified) => {
  const colors = { hadir_tepat_waktu: '#10b981', hadir_terlambat: '#f59e0b', mangkir: '#ef4444' };
  const color = colors[status] || '#6b7280';
  const borderColor = isInRadius ? color : '#ef4444';
  const faceIcon = faceVerified ? '<circle cx="12" cy="12" r="3" fill="white"/>' : '';
  return new L.DivIcon({
    className: 'custom-marker',
    html: `<div style="background: ${color}; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid ${borderColor}; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
        <circle cx="12" cy="7" r="4"></circle><path d="M5.5 21a8.38 8.38 0 0 1 13 0"></path>${faceIcon}
      </svg>
    </div>`,
    iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -32]
  });
};

const MapController = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => { if (center && center[0] && center[1]) map.setView(center, zoom); }, [center, zoom, map]);
  return null;
};

const ASMAT_CENTER = [-5.38, 138.46];
const ASMAT_ZOOM = 9;

const PetaPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [absensiList, setAbsensiList] = useState([]);
  const [sekolahList, setSekolahList] = useState([]);
  const [instansiList, setInstansiList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState(ASMAT_CENTER);
  const [mapZoom, setMapZoom] = useState(ASMAT_ZOOM);
  const [filterOrganisasi, setFilterOrganisasi] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isToday, setIsToday] = useState(true);

  useEffect(() => { loadData(); }, [selectedDate]);

  useEffect(() => {
    let interval;
    if (autoRefresh && isToday) interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, isToday]);

  useEffect(() => {
    const today = new Date();
    const isSameDay = selectedDate.toDateString() === today.toDateString();
    setIsToday(isSameDay);
    if (!isSameDay) setAutoRefresh(false);
  }, [selectedDate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const today = new Date();
      const isSameDay = selectedDate.toDateString() === today.toDateString();
      
      const absensi = isSameDay
        ? await getTodayAbsensiForMap()
        : await getAbsensiMapByDate(selectedDate.toISOString().split('T')[0]);
      
      const [sekolah, instansi] = await Promise.all([
        getSekolahList(),
        getInstansiList().catch(() => [])
      ]);
      
      setAbsensiList(absensi);
      setSekolahList(sekolah);
      setInstansiList(instansi);

      // Calculate center from all locations with attendance
      const allOrgs = [
        ...sekolah.filter(s => absensi.some(a => a.sekolah_id === s.id)),
        ...instansi.filter(i => absensi.some(a => a.instansi_id === i.id))
      ];
      if (allOrgs.length > 0) {
        const avgLat = allOrgs.reduce((sum, o) => sum + o.latitude, 0) / allOrgs.length;
        const avgLng = allOrgs.reduce((sum, o) => sum + o.longitude, 0) / allOrgs.length;
        setMapCenter([avgLat, avgLng]);
        setMapZoom(12);
      }

      const dateLabel = isSameDay ? 'hari ini' : format(selectedDate, 'd MMMM yyyy', { locale: idLocale });
      toast.success(`Data dimuat: ${absensi.length} absensi ${dateLabel}`, { duration: 2000 });
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Gagal memuat data peta');
    } finally {
      setLoading(false);
    }
  };

  const filteredAbsensi = absensiList.filter(a => {
    if (filterOrganisasi !== 'all') {
      if (filterOrganisasi === '_instansi') {
        if (!a.instansi_id) return false;
      } else if (filterOrganisasi === '_sekolah') {
        if (!a.sekolah_id) return false;
      } else {
        if (a.sekolah_id !== filterOrganisasi && a.instansi_id !== filterOrganisasi) return false;
      }
    }
    if (filterStatus !== 'all' && a.status !== filterStatus) return false;
    return true;
  });

  const stats = {
    total: filteredAbsensi.length,
    tepatWaktu: filteredAbsensi.filter(a => a.status === 'hadir_tepat_waktu').length,
    terlambat: filteredAbsensi.filter(a => a.status === 'hadir_terlambat').length,
    luarRadius: filteredAbsensi.filter(a => !a.dalam_radius).length,
    faceVerified: filteredAbsensi.filter(a => a.face_verified).length
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="peta-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-indigo-950">Peta Absensi</h1>
            <p className="text-gray-500">
              {isToday ? 'Visualisasi lokasi absensi hari ini' : `History absensi: ${format(selectedDate, 'd MMMM yyyy', { locale: idLocale })}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isToday && (
            <Button variant={autoRefresh ? "default" : "outline"} size="sm" onClick={() => setAutoRefresh(!autoRefresh)} className={autoRefresh ? "bg-emerald-600" : "border-indigo-200"}>
              <Clock className="w-4 h-4 mr-1" />{autoRefresh ? "Auto ON" : "Auto OFF"}
            </Button>
          )}
          <Button variant="outline" onClick={loadData} className="border-indigo-200">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4 border-indigo-100/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" /><span className="text-sm font-medium">Filter:</span>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={`w-[200px] justify-start text-left font-normal ${!isToday ? 'bg-indigo-50 border-indigo-300' : ''}`} data-testid="date-picker-trigger">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {isToday ? <span>Hari Ini</span> : <span className="flex items-center gap-1"><History className="w-3 h-3" />{format(selectedDate, 'd MMM yyyy', { locale: idLocale })}</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={selectedDate} onSelect={(date) => date && setSelectedDate(date)} disabled={(date) => date > new Date()} initialFocus />
                <div className="p-2 border-t">
                  <Button variant="ghost" size="sm" className="w-full" onClick={() => setSelectedDate(new Date())}>Kembali ke Hari Ini</Button>
                </div>
              </PopoverContent>
            </Popover>
            
            <Select value={filterOrganisasi} onValueChange={setFilterOrganisasi}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Semua Organisasi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Organisasi</SelectItem>
                <SelectItem value="_sekolah">Semua Sekolah</SelectItem>
                <SelectItem value="_instansi">Semua Instansi</SelectItem>
                {sekolahList.length > 0 && <div className="px-2 py-1 text-xs text-gray-400 font-medium">--- Sekolah ---</div>}
                {sekolahList.map(s => <SelectItem key={s.id} value={s.id}>{s.nama}</SelectItem>)}
                {instansiList.length > 0 && <div className="px-2 py-1 text-xs text-gray-400 font-medium">--- Instansi ---</div>}
                {instansiList.map(i => <SelectItem key={i.id} value={i.id}>{i.nama}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Semua Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="hadir_tepat_waktu">Tepat Waktu</SelectItem>
                <SelectItem value="hadir_terlambat">Terlambat</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card className="mb-4 border-indigo-100/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-indigo-950 border-2 border-white shadow flex items-center justify-center">
                <School className="w-3 h-3 text-white" />
              </div>
              <span>Sekolah</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded-full bg-[#1e3a5f] border-2 border-white shadow flex items-center justify-center">
                <Building2 className="w-3 h-3 text-white" />
              </div>
              <span>Instansi</span>
            </div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 rounded-full bg-emerald-500" /><span>Tepat Waktu</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 rounded-full bg-amber-500" /><span>Terlambat</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 rounded-full bg-gray-400 border-2 border-red-500" /><span>Luar Radius</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 rounded-full bg-blue-200 border border-blue-400" /><span>Area Geofence</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Map */}
      <Card className="border-indigo-100/50 overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="h-[500px] flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mx-auto mb-2" />
                <p className="text-gray-500">Memuat peta...</p>
              </div>
            </div>
          ) : (
            <div className="h-[500px]">
              <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                <MapController center={mapCenter} zoom={mapZoom} />
                <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                {/* School markers */}
                {sekolahList
                  .filter(s => filterOrganisasi === 'all' || filterOrganisasi === '_sekolah' || s.id === filterOrganisasi)
                  .map((sekolah) => (
                  <React.Fragment key={`s-${sekolah.id}`}>
                    <Circle center={[sekolah.latitude, sekolah.longitude]} radius={sekolah.radius_geofence || 100}
                      pathOptions={{ color: '#3b82f6', fillColor: '#93c5fd', fillOpacity: 0.2, weight: 2 }} />
                    <Marker position={[sekolah.latitude, sekolah.longitude]} icon={schoolIcon}>
                      <Popup>
                        <div className="text-sm min-w-[200px]">
                          <p className="font-bold text-indigo-950 text-base">{sekolah.nama}</p>
                          <p className="text-gray-600 mt-1">{sekolah.alamat}</p>
                          <div className="mt-2 pt-2 border-t">
                            <p className="text-gray-500"><MapPin className="w-3 h-3 inline mr-1" />Radius: {sekolah.radius_geofence || 100}m</p>
                            <p className="text-gray-500"><Users className="w-3 h-3 inline mr-1" />Absensi: {absensiList.filter(a => a.sekolah_id === sekolah.id).length}</p>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  </React.Fragment>
                ))}

                {/* Instansi markers */}
                {instansiList
                  .filter(i => filterOrganisasi === 'all' || filterOrganisasi === '_instansi' || i.id === filterOrganisasi)
                  .map((inst) => (
                  <React.Fragment key={`i-${inst.id}`}>
                    <Circle center={[inst.latitude, inst.longitude]} radius={inst.radius_geofence || 200}
                      pathOptions={{ color: '#1e3a5f', fillColor: '#60a5fa', fillOpacity: 0.15, weight: 2, dashArray: '6 4' }} />
                    <Marker position={[inst.latitude, inst.longitude]} icon={instansiIcon}>
                      <Popup>
                        <div className="text-sm min-w-[200px]">
                          <p className="font-bold text-slate-800 text-base">{inst.nama}</p>
                          <p className="text-gray-600 mt-1">{inst.alamat}</p>
                          <div className="mt-2 pt-2 border-t">
                            <p className="text-gray-500"><MapPin className="w-3 h-3 inline mr-1" />Radius: {inst.radius_geofence || 200}m</p>
                            <p className="text-gray-500"><Users className="w-3 h-3 inline mr-1" />Absensi: {absensiList.filter(a => a.instansi_id === inst.id).length}</p>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  </React.Fragment>
                ))}

                {/* Attendance markers */}
                {filteredAbsensi.map((absen) => (
                  <Marker key={absen.id} position={[absen.latitude, absen.longitude]}
                    icon={createAttendanceIcon(absen.status, absen.dalam_radius, absen.face_verified)}>
                    <Popup>
                      <div className="text-sm min-w-[220px]">
                        <div className="flex items-start gap-3 mb-2">
                          {absen.foto_url && <img src={absen.foto_url} alt="" className="w-14 h-14 rounded-lg object-cover border" />}
                          <div>
                            <p className="font-bold text-indigo-950">{absen.guru_nama}</p>
                            <p className="text-xs text-gray-500">{absen.guru_nip}</p>
                            <Badge className={`text-xs mt-1 ${getStatusColor(absen.status)}`}>{getStatusLabel(absen.status)}</Badge>
                          </div>
                        </div>
                        <div className="space-y-1 text-gray-600">
                          <p><Clock className="w-3 h-3 inline mr-1" /><span className="font-medium">Waktu:</span> {formatTime(absen.waktu_absen)}</p>
                          <p>
                            {absen.organisasi_type === 'instansi'
                              ? <><Building2 className="w-3 h-3 inline mr-1" /><span className="font-medium">Instansi:</span></>
                              : <><School className="w-3 h-3 inline mr-1" /><span className="font-medium">Sekolah:</span></>
                            } {absen.organisasi_nama}
                          </p>
                          <p><MapPin className="w-3 h-3 inline mr-1" /><span className="font-medium">Akurasi GPS:</span> {Math.round(absen.akurasi_gps)}m</p>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t">
                          <Badge className={absen.dalam_radius ? "bg-emerald-100 text-emerald-700 text-xs" : "bg-red-100 text-red-700 text-xs"}>
                            {absen.dalam_radius ? "Dalam Radius" : "Luar Radius"}
                          </Badge>
                          <Badge className={absen.face_verified ? "bg-blue-100 text-blue-700 text-xs" : "bg-gray-100 text-gray-600 text-xs"}>
                            {absen.face_verified ? <><ShieldCheck className="w-3 h-3 mr-1" />Wajah OK</> : <><ShieldAlert className="w-3 h-3 mr-1" />No Face</>}
                          </Badge>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
        <Card className="border-indigo-100/50"><CardContent className="p-4 text-center"><Users className="w-6 h-6 mx-auto text-indigo-600 mb-1" /><p className="text-2xl font-bold text-indigo-950">{stats.total}</p><p className="text-xs text-gray-500">Total Absensi</p></CardContent></Card>
        <Card className="border-indigo-100/50"><CardContent className="p-4 text-center"><Clock className="w-6 h-6 mx-auto text-emerald-600 mb-1" /><p className="text-2xl font-bold text-emerald-600">{stats.tepatWaktu}</p><p className="text-xs text-gray-500">Tepat Waktu</p></CardContent></Card>
        <Card className="border-indigo-100/50"><CardContent className="p-4 text-center"><Clock className="w-6 h-6 mx-auto text-amber-600 mb-1" /><p className="text-2xl font-bold text-amber-600">{stats.terlambat}</p><p className="text-xs text-gray-500">Terlambat</p></CardContent></Card>
        <Card className="border-indigo-100/50"><CardContent className="p-4 text-center"><MapPin className="w-6 h-6 mx-auto text-red-600 mb-1" /><p className="text-2xl font-bold text-red-600">{stats.luarRadius}</p><p className="text-xs text-gray-500">Luar Radius</p></CardContent></Card>
        <Card className="border-indigo-100/50"><CardContent className="p-4 text-center"><ShieldCheck className="w-6 h-6 mx-auto text-blue-600 mb-1" /><p className="text-2xl font-bold text-blue-600">{stats.faceVerified}</p><p className="text-xs text-gray-500">Face Verified</p></CardContent></Card>
      </div>

      {filteredAbsensi.length === 0 && !loading && (
        <Alert className="mt-4 border-amber-200 bg-amber-50">
          <AlertDescription className="text-amber-800">Belum ada data absensi untuk hari ini dengan filter yang dipilih.</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default PetaPage;
